import React from 'react';
import { SITE_CONFIG, trackEvent } from '../config/site';
import { ShieldCheck, Mail, LogIn, ExternalLink } from 'lucide-react';
import { LegalDocType } from './LegalModal';

interface FooterProps {
  onOpenLegal: (type: NonNullable<LegalDocType>) => void;
  onOpenWaitlist: () => void;
}

export const Footer: React.FC<FooterProps> = ({ onOpenLegal, onOpenWaitlist }) => {
  const handleLogin = () => {
    trackEvent('login_click', { location: 'footer' });
  };

  const handleWaitlist = () => {
    trackEvent('hero_cta', { location: 'footer_nav' });
    onOpenWaitlist();
  };

  return (
    <footer className="bg-brand-bg border-t border-brand-border py-16 text-xs text-brand-textMuted">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 pb-12 border-b border-brand-border/60">
          {/* Col 1: Brand & Positioning */}
          <div className="md:col-span-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-primary to-brand-cyan flex items-center justify-center">
                <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                </svg>
              </div>
              <span className="font-heading font-bold text-lg text-white">InfoForge</span>
              <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-brand-surface border border-brand-border text-brand-cyan">
                Private Beta
              </span>
            </div>

            <p className="text-brand-textMuted leading-relaxed max-w-sm">
              Plataforma de engenharia de infoprodutos assistida por inteligência artificial. Transforme teses e conhecimento técnico em produtos digitais completos com consistência ponta a ponta.
            </p>

            <div className="flex items-center gap-2 text-[11px] text-brand-textSubtle">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
              <span>Infraestrutura Segura · DB8 Intelligence</span>
            </div>
          </div>

          {/* Col 2: Quick Navigation */}
          <div className="md:col-span-3 space-y-3">
            <h4 className="text-xs font-bold text-white uppercase tracking-wider">Navegação</h4>
            <ul className="space-y-2.5">
              <li><a href="#como-funciona" className="hover:text-white transition-colors">Como Funciona</a></li>
              <li><a href="#entregaveis" className="hover:text-white transition-colors">Catálogo de Entregáveis</a></li>
              <li><a href="#consistencia" className="hover:text-white transition-colors">Linhagem & Consistência</a></li>
              <li><a href="#planos" className="hover:text-white transition-colors">Planos & Créditos</a></li>
              <li><a href="#faq" className="hover:text-white transition-colors">Perguntas Frequentes</a></li>
              <li>
                <button
                  type="button"
                  onClick={handleWaitlist}
                  className="text-brand-cyan hover:underline transition-colors text-left font-medium"
                >
                  Solicitar Acesso ao Beta →
                </button>
              </li>
            </ul>
          </div>

          {/* Col 3: Legal & Privacy */}
          <div className="md:col-span-2 space-y-3">
            <h4 className="text-xs font-bold text-white uppercase tracking-wider">Institucional</h4>
            <ul className="space-y-2.5">
              <li>
                <button
                  type="button"
                  onClick={() => onOpenLegal('terms')}
                  className="hover:text-white transition-colors text-left"
                >
                  Termos de Uso
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => onOpenLegal('privacy')}
                  className="hover:text-white transition-colors text-left"
                >
                  Privacidade (LGPD)
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => onOpenLegal('ai')}
                  className="hover:text-white transition-colors text-left"
                >
                  Declaração de IA
                </button>
              </li>
              <li>
                <a
                  href={`mailto:${SITE_CONFIG.supportEmail}`}
                  className="hover:text-white transition-colors flex items-center gap-1.5"
                >
                  <Mail className="w-3.5 h-3.5" />
                  Suporte Técnico
                </a>
              </li>
            </ul>
          </div>

          {/* Col 4: App Access */}
          <div className="md:col-span-2 space-y-3">
            <h4 className="text-xs font-bold text-white uppercase tracking-wider">Acesso ao SaaS</h4>
            <p className="text-[11px] text-brand-textSubtle leading-relaxed">
              O ambiente de produção requer convite ativo emitido pela equipe.
            </p>
            <a
              href={SITE_CONFIG.loginUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleLogin}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-brand-surface border border-brand-border text-xs font-medium text-white hover:border-brand-primary/50 transition-all"
            >
              <LogIn className="w-3.5 h-3.5" />
              <span>Entrar no Dashboard</span>
              <ExternalLink className="w-3 h-3 text-brand-textSubtle" />
            </a>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
          <p className="text-[11px] text-brand-textSubtle">
            © {SITE_CONFIG.year} InfoForge · DB8 Intelligence. Todos os direitos reservados.
          </p>
          <div className="flex items-center gap-4 text-[11px] text-brand-textSubtle">
            <span>Ambiente: Sales Authority</span>
            <span>•</span>
            <span>Versão: 1.0.0-beta</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
